import { Form } from "antd";
import { TrainingForm } from "./TrainingFormDialog.default";
import { Training } from "../../repository/data.default";
import { useEffect, useState } from "react";
import {
  addTraining,
  deleteTraining,
  getCompanies,
  getCompanyCollaborators,
} from "../../repository/data.repository";
import DynamicForm from "../../controls/DynamicForm";
import InModal from "../../controls/InModal";
import { modalTitle } from "../../controls/InModal.default";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";
import { dateTimeFormat } from "../../utils/date";
import { getProblemMessage } from "../../utils/http";
import {
  createSearchRequest,
  createScopedSearchRequest,
} from "../../hooks/useSearchRequest";
import dayjs from "dayjs";
import { DynamicControl } from "../../controls/DynamicForm.default";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";

export interface TrainingFormDialogProps {
  readonly open?: boolean;
  // Plan a new training, or confirm a delete. Post-creation edits + lifecycle live in the Manage view.
  readonly mode: "add" | "delete";
  readonly record?: Training;
  // On create the fresh training is handed back so the caller can open it straight in the Manage view.
  readonly onFinish?: (created?: Training) => void;
  readonly onCancel?: () => void;
}

function TrainingFormDialog(props: TrainingFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<TrainingForm>([], form);

  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Affiliates always host their own trainings (set server-side) — only admins pick the facilitator.
  const isAffiliate = useSelector(authorizeSelectors.isAffiliate);

  useEffect(() => {
    if (!props.open) {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  function onSubmit(): void {
    setLoading(true);
    setErrorMessage("");
    if (props.mode === "add") {
      const { title, description, location, startDate, endDate, company, host } =
        values;
      addTraining({
        title,
        description,
        location,
        startDate: dayjs(startDate).toDate(),
        endDate: dayjs(endDate).toDate(),
        companyUid: company.value,
        hostUid: host?.value,
      })
        .then((created) => {
          setLoading(false);
          props.onFinish?.(created);
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "delete") {
      deleteTraining(props.record?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setLoading(false);
    setValid(false);
    setErrorMessage("");
    form.resetFields();
  }

  // The host must be an affiliate assigned to the training's company — the CompanyCollaborators join
  // returns all of a company's assigned affiliates (consultants + collaborators). Empty until a company
  // is picked; `dependsOn` clears a stale host when the company changes.
  const hostsRequest = createScopedSearchRequest(
    getCompanyCollaborators,
    "userUserName",
    "companyUid",
    () => values?.company?.value,
    undefined,
    { valueField: "userUid", subDisplayField: "companyName" },
  );

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Training", props.record?.title)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode !== "delete" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "textbox",
                    name: "title",
                    label: "Title",
                    rules: [
                      {
                        required: true,
                        message: "Title is required",
                      },
                    ],
                  },
                  {
                    type: "areabox",
                    name: "description",
                    label: "Description",
                  },
                  {
                    type: "textbox",
                    name: "location",
                    label: "Location",
                  },
                  {
                    type: "datetimepicker",
                    name: "startDate",
                    label: "Starts",
                    format: dateTimeFormat,
                    rules: [
                      {
                        required: true,
                        message: "Start is required",
                      },
                    ],
                  },
                  {
                    type: "datetimepicker",
                    name: "endDate",
                    label: "Ends",
                    format: dateTimeFormat,
                    rules: [
                      { required: true, message: "End is required" },
                      ({ getFieldValue }) => ({
                        validator(_, endDate) {
                          const startDate = getFieldValue("startDate");
                          if (!endDate || !startDate || startDate < endDate) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("End date cannot be less than start date"),
                          );
                        },
                      }),
                    ],
                  },
                  {
                    type: "combobox",
                    name: "company",
                    label: "Company",
                    request: createSearchRequest(getCompanies, "name"),
                    rules: [
                      {
                        required: true,
                        message: "Company is required",
                      },
                    ],
                  },
                  // Admins name the facilitating affiliate; affiliates are auto-hosted server-side.
                  ...((!isAffiliate
                    ? [
                        {
                          type: "combobox",
                          name: "host",
                          label: "Lead trainer",
                          dependsOn: ["company"],
                          request: hostsRequest,
                          rules: [
                            {
                              required: true,
                              message: "Lead trainer is required",
                            },
                          ],
                        },
                      ]
                    : []) as DynamicControl[]),
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="training"
          entityName={props.record?.title}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default TrainingFormDialog;
